// A broadside from the beloved ship on a profile: where the guns flash and
// smoke along the hull, and the boom of each gun. The boom is a recording of
// a cannon firing — "cannon fire" by Thimras, CC0 (public domain), from
// https://opengameart.org/content/cannon-fire — served as
// /audio/cannon.m4a and only fetched the first time someone fires. Every
// 12th click on a ship (all visitors together, counted by the server — see
// SINK_EVERY in api/src/profiles/broadsides.ts) sinks it instead: /audio/sink.m4a mixes a
// "We're sinking!" shouted by the clan's own Hartje, "Ship sinking" by
// Thimras (CC0, opengameart.org/node/136710) and "Water Bubbles" by Liecio
// (Pixabay Content License,
// pixabay.com/sound-effects/nature-water-bubbles-257594). Sound only ever
// starts from a click.

/** Seconds before the guns may fire again. */
export const RELOAD_SECONDS = 10;

/** How long the ship takes to go under, and how long the sea stays empty before a new one sails in. */
export const SINK_SECONDS = 9;
export const NEW_SHIP_AFTER_SECONDS = 11;

/** Where the hull is in a ship picture, as shares of its width and height. */
export interface HullBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Used when a picture can't be read: roughly where the hull sits in the rendered ship pictures. */
export const DEFAULT_HULL: HullBox = { left: 0.28, right: 0.87, top: 0.9, bottom: 1 };

/**
 * Pure: finds the hull in a ship picture (RGBA pixels, cut just under the
 * waterline). From the bottom row up, rows where the ship is solid over a
 * long stretch (30% of the width) are hull; the first row above where that
 * breaks off is the top of the hull — sails and rigging never form such
 * long solid runs. The hull's length is the median run, so the bowsprit and
 * stern gallery don't stretch it.
 */
export function findHull(pixels: Uint8ClampedArray, width: number, height: number): HullBox | null {
  const rows: { y: number; start: number; end: number }[] = [];
  const lowest = Math.max(0, height - Math.floor(height * 0.6));
  for (let y = height - 1; y >= lowest; y--) {
    let best = 0;
    let bestStart = 0;
    let run = 0;
    let start = 0;
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3]! > 160) {
        if (run === 0) start = x;
        run++;
        if (run > best) {
          best = run;
          bestStart = start;
        }
      } else {
        run = 0;
      }
    }
    if (best >= width * 0.3) rows.push({ y, start: bestStart, end: bestStart + best });
    else if (rows.length && y < rows[rows.length - 1]!.y - 3) break;
  }
  if (rows.length === 0) return null;
  const starts = rows.map((r) => r.start).sort((a, b) => a - b);
  const ends = rows.map((r) => r.end).sort((a, b) => a - b);
  const mid = Math.floor(rows.length / 2);
  return {
    left: starts[mid]! / width,
    right: ends[mid]! / width,
    top: Math.min(...rows.map((r) => r.y)) / height,
    bottom: (Math.max(...rows.map((r) => r.y)) + 1) / height,
  };
}

export interface Gun {
  /** Position in the ship picture, as shares of its width (0 = bow end) and height (0 = top). */
  x: number;
  y: number;
  /** Seconds after the click that this gun fires. */
  delay: number;
}

/**
 * Pure: the guns of one broadside for a ship with `guns` guns over `decks`
 * gun decks, placed on its hull. A broadside is half the guns; more than a
 * dozen would only merge into one cloud, so it is a few for a cutter up to
 * 12 for a first rate. They ripple from bow (left — the ship faces left) to
 * stern along the hull, one row per deck (at most three) above the waterline.
 */
export function broadsideGuns(guns: number, decks: number, hull: HullBox = DEFAULT_HULL): Gun[] {
  const count = Math.min(12, Math.max(3, Math.round(guns / 8)));
  const rows = Math.min(3, Math.max(1, decks));
  const length = hull.right - hull.left;
  const height = hull.bottom - hull.top;
  const out: Gun[] = [];
  for (let i = 0; i < count; i++) {
    const along = count === 1 ? 0.5 : i / (count - 1);
    const row = i % rows;
    out.push({
      x: hull.left + length * (0.1 + along * 0.8),
      // the lowest deck sits a little above the waterline, higher decks above it
      y: hull.bottom - height * (rows === 1 ? 0.45 : 0.3 + (row * 0.45) / (rows - 1)),
      delay: i * 0.13 + (i % 2) * 0.03,
    });
  }
  return out;
}

/** Reads the hull out of a loaded ship picture; null if it can't be read. */
export function hullOfImage(img: HTMLImageElement): HullBox | null {
  try {
    const width = 240;
    const height = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * width));
    const c = document.createElement('canvas');
    c.width = width;
    c.height = height;
    const g = c.getContext('2d', { willReadFrequently: true });
    if (!g) return null;
    g.drawImage(img, 0, 0, width, height);
    return findHull(g.getImageData(0, 0, width, height).data, width, height);
  } catch {
    return null;
  }
}

const CANNON_URL = '/audio/cannon.m4a';
const SINK_URL = '/audio/sink.m4a';

let audio: AudioContext | null = null;
const recordings = new Map<string, Promise<AudioBuffer>>();

/** A recording, fetched and decoded once. */
function load(ctx: BaseAudioContext, url: string): Promise<AudioBuffer> {
  let recording = recordings.get(url);
  if (!recording) {
    recording = fetch(url)
      .then((r) => r.arrayBuffer())
      .then((data) => ctx.decodeAudioData(data));
    // a failed load may be retried next time
    recording.catch(() => recordings.delete(url));
    recordings.set(url, recording);
  }
  return recording;
}

/** The page's audio context, created (or woken) from a click. */
function context(): AudioContext | null {
  const AudioCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) return null;
  audio ??= new AudioCtor();
  if (audio.state === 'suspended') void audio.resume();
  return audio;
}

/** Plays the ship going down: hull breaking up, then the sea pouring in and bubbles. Only call from a click. */
export function playSink(): void {
  try {
    const ctx = context();
    if (!ctx) return;
    load(ctx, SINK_URL)
      .then((recording) => {
        const source = ctx.createBufferSource();
        source.buffer = recording;
        const level = ctx.createGain();
        level.gain.value = 0.8;
        source.connect(level).connect(ctx.destination);
        source.start();
      })
      .catch(() => {});
  } catch {
    // no sound is fine: the ship still goes down
  }
}

/**
 * Schedules one shot of the recording per gun at its delay, each a little
 * different in pitch and loudness so a broadside never sounds like one
 * clip repeated; the echoes of the early shots roll under the later ones.
 */
export function scheduleBroadside(
  ctx: BaseAudioContext,
  destination: AudioNode,
  shot: AudioBuffer,
  guns: readonly Gun[],
  start: number,
): void {
  const master = ctx.createGain();
  master.gain.value = 0.7;
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -8;
  limiter.ratio.value = 10;
  master.connect(limiter).connect(destination);
  for (const gun of guns) {
    const source = ctx.createBufferSource();
    source.buffer = shot;
    // a heavier or lighter gun: slightly lower or higher, and with it a touch slower or faster
    source.playbackRate.value = 0.86 + Math.random() * 0.2;
    const level = ctx.createGain();
    level.gain.value = 0.55 + Math.random() * 0.35;
    source.connect(level).connect(master);
    source.start(start + gun.delay);
  }
}

/** Plays the broadside, one cannon shot per gun at its delay. Only call from a click. */
export function playBroadside(guns: readonly Gun[]): void {
  try {
    const ctx = context();
    if (!ctx) return;
    const clicked = ctx.currentTime;
    load(ctx, CANNON_URL)
      .then((shot) => {
        // keep the guns in step with the smoke, even when the first load took a moment
        const late = ctx.currentTime - clicked;
        scheduleBroadside(
          ctx,
          ctx.destination,
          shot,
          guns.map((g) => ({ ...g, delay: Math.max(0, g.delay - late) })),
          ctx.currentTime + 0.01,
        );
      })
      .catch(() => {
        // no sound is fine: the smoke still shows
      });
  } catch {
    // no sound is fine: the smoke still shows
  }
}
